import React, { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from 'react-apollo';
import ReactGA from 'react-ga';
import { useLocation } from 'react-router-dom';

import { defineMessages, substitute } from '@sm/intl';
import {
  BasePage,
  Helmet,
  StaticContext,
  ErrorCard,
  DesktopScreen,
  MobileScreen,
  FourOhFourError,
} from '@sm/webassets';
import { Grid, Row, Col, Typography, Banner, Button, Card, Tabs, Tab } from '@sm/wds-react';
import {
  IconTag,
  IconEmail,
  IconGlobe,
  IconLock,
  IconLogoWhatsApp,
  IconMegaphone,
  IconNews,
  IconCheck,
  IconGear,
  IconUserFilled,
} from '@sm/wds-icons';
import { sanitizeString, getClientEnvironmentDetails } from '@sm/utils';
import { MetricsTracker, generateMetricsAttribute, USER_EVENTS } from '@sm/metrics';

import AppDirectoryAppDetailsSkeleton from './AppDirectoryAppDetailsSkeleton';

import './app-directory-app-details.scss';
import publishedApplicationListingDetails from './published-application-listing-details.graphql';
import previewApplicationListingDetails from './preview-application-listing-details.graphql';
import RemoveApp from '../../components/RemoveApp';
import AppPlaceHolderImg from '../../static/images/app-placeholder.png';

import UCSModule from '../../components/UCSModule';
import getCanonicalUrlOrNoIndex from '~app/helpers/getCanonicalUrlOrNoIndex';
import waitForUsabilla from '~app/helpers/waitForUsabilla';
import MediaCarousel from './MediaCarousel';
import AppDetailsItem from './AppDetailsItem';
import ModernHeader from '../../components/ModernHeader';

const COPY = defineMessages({
  APP_DETAILS_HEADER: {
    id: 'Details.AppDetails',
    defaultMessage: 'App Details',
    description: '[Type: Header][Vis.: low] - A header preceding the full description of the current app',
  },
  REQUIRED_ACCESS_LABEL: {
    id: 'Details.RequiredAccess',
    defaultMessage: 'This app will require access to:',
    description:
      '[Type: Label][Vis.: low] - A label preceding a list of requirements before the current application could be installed',
  },
  REQUIRED_ACCOUNT_LABEL: {
    id: 'Details.RequiredAccount',
    defaultMessage: 'This app will require an additional {accountType} account:',
    description:
      '[Type: Label][Vis.: low] - A label preceding a description of an external account required by the app',
  },
  INSTALLED_LABEL: {
    id: 'Details.Installed',
    defaultMessage: 'Installed',
    description:
      '[Type: Label][Vis.: low] - A label indicating that the user has the current visible application installed',
  },
  INSTALL_LABEL: {
    id: 'Details.Install',
    defaultMessage: 'Install',
    description:
      '[Type: Label][Vis.: low] - A link/button that will redirect the user to the install page for the current app',
  },
  VISIT_SITE_LABEL: {
    id: 'Details.VisitSite',
    defaultMessage: 'Visit Site',
    description:
      '[Type: Link][Vis.: med] - A link/button that will redirect the user to the site of the application they are trying to install',
  },
  LAUNCH_LABEL: {
    id: 'Details.Launch',
    defaultMessage: 'Launch',
    description:
      '[Type: Link][Vis.: med] - A link/button that will redirect the user to the current visible application',
  },
  UPGRADE_LABEL: {
    id: 'Details.Upgrade',
    defaultMessage: 'Upgrade',
    description:
      '[Type: Link][Vis.: med] - A link/button that will redirect the user to the upgrade page for the visible application',
  },
  REMOVE_APP: {
    id: 'Details.RemoveApp',
    defaultMessage: 'Remove App',
    description:
      '[Type: Link][Vis.: low] - A link/button that will open a dialog requesting the user to confirm the removal of the current app',
  },
});

function getLangFromUrl(search) {
  return new URLSearchParams(search).get('lang') || 'en';
}

export const useSearchParams = () => {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
};

const AppDirectoryAppDetails = ({
  match: {
    params: { appId },
  },
  location: { search, pathname },
  preview,
}) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [pageTracked, setPageTracked] = useState(false);
  const [googleTracked, setGoogleTracked] = useState(false);
  const { userAgent, user } = useContext(StaticContext);
  const { isDesktop } = getClientEnvironmentDetails(userAgent);
  const {
    environment: { subdomain, domain, tld },
    url,
  } = useContext(StaticContext);
  const language = getLangFromUrl(search);
  const { noIndex, canonicalUrl } = getCanonicalUrlOrNoIndex(subdomain, domain, tld, url);
  const urlParams = useSearchParams();

  const getEventNameFromSource = source => {
    switch (source) {
      case 'collector_list':
        return 'list';
      case 'website_collector':
        return 'weblink';
      case 'weblink_collector':
        return 'embedded';
      default:
        return '';
    }
  };

  const addCollectEventInfo = appDetails => {
    const source = urlParams.get('ut_source');
    return source
      ? {
          eventName: `collect ${getEventNameFromSource(source)} user interaction`,
          interactionType: 'click',
          interactionLocation: 'integration card',
          integrationOptionClicked: appDetails.name,
        }
      : {};
  };

  const onCompleted = appDetails => {
    if (appDetails && appDetails.details.googleAnalyticsTrackingId && !googleTracked) {
      const trackerName = 'app-dir-details-pageview';
      ReactGA.initialize(appDetails.details.googleAnalyticsTrackingId, {
        name: trackerName, // this is tracker name in case there are other trackers on the page
      });
      ReactGA.pageview(pathname, [trackerName]);
      setGoogleTracked(true);
    }

    if (appDetails && !pageTracked) {
      MetricsTracker.track({
        name: USER_EVENTS.COMPONENT_ADD,
        data: {
          actionFlow: 'browsing',
          actionType: 'app_details.page_view',
          appId: appDetails.id,
          appName: appDetails.name,
        },
      });
      setPageTracked(true);
    }
  };

  const getAppDetails = (data = {}) =>
    preview ? data.previewApplicationListingDetails : data.publishedApplicationListingDetails;

  const {
    loading,
    error,
    refetch,
    data: queryData = {},
  } = useQuery(preview ? previewApplicationListingDetails : publishedApplicationListingDetails, {
    variables: { language, appId },
    onCompleted: (data = {}) => onCompleted(getAppDetails(data)),
  });
  const appDetails = getAppDetails(queryData);

  useEffect(() => {
    waitForUsabilla(() => {
      window.usabilla_live('show');
      window.usabilla_live('setForm', 'AppDirectoryForm');
    });
  }, []);

  return (
    <BasePage pageId="App Directory - Details" includeHeader={false} legacyWeb="papi">
      <ModernHeader />
      <If condition={user.isAuthenticated}>
        <UCSModule moduleName="top_bar" customLoader={<div />} />
      </If>

      <Choose>
        <When condition={error}>
          <ErrorCard data-testid="Details__AppDirectoryError" />
        </When>
        <When condition={loading}>
          <AppDirectoryAppDetailsSkeleton data-testid="Details__AppDirectorySkeleton" />
        </When>
        <When condition={!appDetails}>
          <div data-testid="Details__AppDirectoryNotFound">
            <FourOhFourError />
          </div>
        </When>
        <Otherwise>
          <Helmet>
            <title>{`${appDetails.name} - SurveyMonkey App Integration`}</title>
            <meta name="description" content={appDetails.tagline} />
            <If condition={!noIndex}>
              <link rel="canonical" href={canonicalUrl} />
            </If>
            <If condition={noIndex}>
              <meta name="robots" content="noindex, nofollow" />
            </If>
          </Helmet>
          <Grid className="wds-m-t-6 sm-app-directory-app-details">
            <Row>
              <Col md={1} xs={12}>
                <img
                  className="sm-app-directory-app-details__logo"
                  src={appDetails.logo ? appDetails.logo : AppPlaceHolderImg}
                  alt={appDetails.name}
                />
              </Col>
              <MobileScreen defaultMatches={!isDesktop}>
                <Col xs={12} className="wds-flex wds-flex--y wds-flex--x-center wds-flex--y-center wds-m-b-5">
                  <Typography variant="pageTitle" className="wds-m-b-4">
                    {appDetails.name}
                  </Typography>
                  <If condition={appDetails.installed}>
                    <Button color="info" size="sm" className="wds-m-b-4">
                      {COPY.INSTALLED_LABEL.defaultMessage}
                    </Button>
                  </If>
                  <Typography color="dark-muted">{appDetails.tagline}</Typography>
                </Col>
              </MobileScreen>
              <DesktopScreen defaultMatches={isDesktop}>
                <Col md={7}>
                  <div className="wds-flex wds-flex--y-center">
                    <Typography variant="pageTitle" className="wds-m-r-3">
                      {appDetails.name}
                    </Typography>
                    <If condition={appDetails.installed}>
                      <Button color="info" size="sm">
                        {COPY.INSTALLED_LABEL.defaultMessage}
                      </Button>
                    </If>
                  </div>
                  <Typography color="dark-muted">{appDetails.tagline}</Typography>
                </Col>
              </DesktopScreen>
              <Col md={4} xs={12}>
                <Choose>
                  <When condition={appDetails.details.linkType === 'VISIT_SITE'}>
                    <Button href={`${appDetails.details.link}`} color="primary" stretched>
                      {COPY.VISIT_SITE_LABEL.defaultMessage}
                    </Button>
                  </When>
                  <When condition={appDetails.details.linkType === 'LAUNCH'}>
                    <Button href={`${appDetails.details.link}`} color="primary" stretched>
                      {COPY.LAUNCH_LABEL.defaultMessage}
                    </Button>
                  </When>
                  <When condition={appDetails.details.linkType === 'UPGRADE'}>
                    <Button href={`${appDetails.details.link}`} color="upgrade" stretched>
                      {COPY.UPGRADE_LABEL.defaultMessage}
                    </Button>
                  </When>
                  <Otherwise>
                    <Button
                      href={`${appDetails.details.link}`}
                      color="primary"
                      stretched
                      data-sm-metrics={generateMetricsAttribute({
                        data: {
                          actionType: 'app_details.install_click',
                          actionFlow: 'browsing',
                          ...addCollectEventInfo(appDetails),
                        },
                      })}
                    >
                      {COPY.INSTALL_LABEL.defaultMessage}
                    </Button>
                  </Otherwise>
                </Choose>
              </Col>
            </Row>
            <Row className="wds-m-t-3">
              <Col md={4} xs={12} mdOrder={1} className="wds-m-b-4">
                <Card
                  noRule
                  title={<Typography variant="cardTitle">{COPY.APP_DETAILS_HEADER.defaultMessage}</Typography>}
                >
                  <If
                    condition={
                      user.isAuthenticated && (appDetails.details.upgradeRequired || appDetails.details.optionalUpgrade)
                    }
                  >
                    <div className="wds-m-b-4">
                      <Banner showIcon>
                        <strong>
                          <If condition={appDetails.details.upgradeRequired}>
                            This app requires an upgraded plan to function
                            <Else />
                            This app requires an upgraded plan to access some functionality
                          </If>
                        </strong>
                      </Banner>
                    </div>
                  </If>
                  {AppDetailsItem(
                    <IconTag />,
                    'Publisher',
                    appDetails.details.websiteUrl,
                    appDetails.details.publisher,
                    search
                  )}
                  <If condition={appDetails.details.supportEmail}>
                    {AppDetailsItem(
                      <IconEmail />,
                      'Email',
                      `mailto:${appDetails.details.supportEmail}`,
                      appDetails.details.supportEmail,
                      search
                    )}
                  </If>
                  <If condition={appDetails.details.supportPhoneNumber}>
                    {AppDetailsItem(
                      <IconLogoWhatsApp />,
                      'Phone',
                      `tel:${appDetails.details.supportPhoneNumber}`,
                      appDetails.details.supportPhoneNumber,
                      search
                    )}
                  </If>
                  <If condition={appDetails.details.supportUrl}>
                    {AppDetailsItem(
                      <IconGlobe />,
                      'Support Website',
                      `${appDetails.details.supportUrl}`,
                      appDetails.details.supportUrl,
                      search
                    )}
                  </If>
                  {AppDetailsItem(
                    <IconLock />,
                    'Privacy Policy',
                    appDetails.details.privacyPolicyUrl,
                    appDetails.details.privacyPolicyUrl,
                    search
                  )}
                  {AppDetailsItem(
                    <IconNews />,
                    'Terms of Service',
                    appDetails.details.termsOfUseUrl,
                    appDetails.details.termsOfUseUrl,
                    search
                  )}
                  <If condition={appDetails.details.blogUrl}>
                    {AppDetailsItem(
                      <IconMegaphone />,
                      'Blog',
                      `${appDetails.details.blogUrl}`,
                      appDetails.details.blogUrl,
                      search
                    )}
                  </If>
                </Card>
                <If condition={appDetails.installed && !appDetails.isIntegration}>
                  <RemoveApp appId={appDetails.id} onSuccess={() => refetch()}>
                    <Button
                      stretched
                      color="muted"
                      className="wds-m-t-3 sm-app-directory-app-details__remove-app-button"
                    >
                      {COPY.REMOVE_APP.defaultMessage}
                    </Button>
                  </RemoveApp>
                </If>
              </Col>
              <Col md={8} xs={12}>
                <MediaCarousel
                  data-testid="Details__MediaCarrousel"
                  media={[].concat(
                    appDetails.details.youtubeUrl
                      ? [
                          {
                            url: appDetails.details.youtubeUrl,
                            type: 'youtube',
                            altText: appDetails.name,
                          },
                        ]
                      : [],
                    appDetails.details.screenshots.map(s => ({
                      url: s,
                      type: 'image',
                      altText: appDetails.name,
                    }))
                  )}
                />
                <div className="wds-m-t-3">
                  {appDetails.details.featureList.map(feature => (
                    <div className="wds-flex wds-flex--y-center" key={feature}>
                      <IconCheck color="dark-muted" />
                      <Typography className="wds-m-l-3" variant="sectionTitle">
                        {feature}
                      </Typography>
                    </div>
                  ))}
                </div>
                <div className="wds-m-t-3 wds-m-b-3">
                  <Tabs color="transparent" onChange={value => setSelectedTab(value)}>
                    <Tab selected={selectedTab === 0} title="Description">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: sanitizeString(appDetails.fullDescription),
                        }}
                      />
                    </Tab>
                    <Tab
                      selected={selectedTab === 1}
                      title="Requirements"
                      data-testid="Details__AppDirectoryRequirementsTab"
                    >
                      <If condition={appDetails.details.requirements}>
                        <Typography variant="cardTitle" component="h3" data-testid="Details__AppDirectoryRequirements">
                          {substitute(COPY.REQUIRED_ACCOUNT_LABEL.defaultMessage, {
                            accountType: appDetails.details.requirements.type,
                          })}
                        </Typography>
                        <ul className="sm-app-directory-app-details__requirements wds-p-l-3 wds-p-t-3">
                          <li className="wds-flex wds-flex--y-center wds-m-b-2">
                            <IconUserFilled color="light-muted" />
                            <a
                              href={appDetails.details.requirements.pricingUrl + search}
                              target="_blank"
                              rel="noreferrer noopener"
                            >
                              <Typography className="wds-m-l-2" variant="link">
                                {appDetails.details.requirements.label}
                              </Typography>
                            </a>
                          </li>
                        </ul>
                        <If condition={appDetails.details.requirements.description}>
                          <p>{appDetails.details.requirements.description}</p>
                        </If>
                      </If>
                      <If condition={appDetails.details.scopes}>
                        <Typography variant="cardTitle" component="h3">
                          {COPY.REQUIRED_ACCESS_LABEL.defaultMessage}
                        </Typography>
                        <ul className="sm-app-directory-app-details__requirements wds-p-l-3 wds-p-t-3">
                          {appDetails.details.scopes.map(scope => (
                            <li className="wds-flex wds-flex--y-center wds-m-b-2" key={scope}>
                              <IconGear color="light-muted" />
                              <span className="wds-m-l-2">{scope}</span>
                            </li>
                          ))}
                        </ul>
                      </If>
                    </Tab>
                  </Tabs>
                </div>
              </Col>
            </Row>
          </Grid>
        </Otherwise>
      </Choose>
    </BasePage>
  );
};

AppDirectoryAppDetails.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      appId: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  location: PropTypes.shape({
    search: PropTypes.string,
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  preview: PropTypes.bool,
};

AppDirectoryAppDetails.defaultProps = {
  preview: false,
};

export default AppDirectoryAppDetails;
